import { useState, useRef } from 'react';
import { adminApi } from '../api/adminApi';
import { Modal } from '@shared-ui/components';
import type { ModuleImportResult, ModuleImportStatus } from '../types';

type Phase = 'configure' | 'dryrun' | 'running' | 'results';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

export function ModuleImportDialog({ isOpen, onClose, onImported }: Props) {
  const [phase, setPhase] = useState<Phase>('configure');
  const [file, setFile] = useState<File | null>(null);
  const [parsedModules, setParsedModules] = useState<Record<string, unknown>[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [dryRunResult, setDryRunResult] = useState<ModuleImportResult | null>(null);
  const [finalResult, setFinalResult] = useState<ModuleImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'created' | 'updated' | 'unchanged' | 'error' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const reset = () => {
    setPhase('configure');
    setFile(null);
    setParsedModules([]);
    setParseError(null);
    setDryRunResult(null);
    setFinalResult(null);
    setError(null);
    setStatusFilter(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParseError(null);

    try {
      const text = await f.text();
      const json = JSON.parse(text);
      const modules = json.modules;
      if (!Array.isArray(modules) || modules.length === 0) {
        setParseError('File must contain a "modules" array with at least one module');
        return;
      }
      setParsedModules(modules);
    } catch {
      setParseError('Invalid JSON file');
    }
  };

  const handleDryRun = async () => {
    setPhase('running');
    setError(null);
    try {
      const result = await adminApi.importModules(parsedModules as never, true);
      setDryRunResult(result);
      setPhase('dryrun');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase('configure');
    }
  };

  const handleImport = async () => {
    setPhase('running');
    setError(null);
    try {
      const result = await adminApi.importModules(parsedModules as never, false);
      setFinalResult(result);
      setPhase('results');
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase('dryrun');
    }
  };

  // Compute summary from parsed modules
  const summary = parsedModules.length > 0 ? {
    total: parsedModules.length,
    modules: parsedModules.filter(m => m.moduleLevel === 'module').length,
    chapters: parsedModules.filter(m => m.moduleLevel === 'chapter').length,
    topics: parsedModules.filter(m => m.moduleLevel === 'topic').length,
    subjects: [...new Set(parsedModules.map(m => m.subject as string))],
  } : null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Modules" size="lg" closeOnOverlayClick={phase === 'configure' || phase === 'results'}>
        <div>
          {error && (
            <div className="import-error-banner">{error}</div>
          )}

          {/* Phase: Configure */}
          {phase === 'configure' && (
            <div className="import-configure">
              <div className="import-file-picker">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose JSON File
                </button>
                {file && <span className="import-file-name">{file.name}</span>}
              </div>

              {parseError && <div className="import-parse-error">{parseError}</div>}

              {summary && (
                <div className="import-summary">
                  <h3>File Summary</h3>
                  <div className="import-summary-grid">
                    <div className="import-stat">
                      <span className="import-stat-value">{summary.total}</span>
                      <span className="import-stat-label">Total</span>
                    </div>
                    <div className="import-stat">
                      <span className="import-stat-value">{summary.modules}</span>
                      <span className="import-stat-label">Modules</span>
                    </div>
                    <div className="import-stat">
                      <span className="import-stat-value">{summary.chapters}</span>
                      <span className="import-stat-label">Chapters</span>
                    </div>
                    <div className="import-stat">
                      <span className="import-stat-value">{summary.topics}</span>
                      <span className="import-stat-label">Topics</span>
                    </div>
                  </div>
                  <p className="import-subjects">
                    Subjects: {summary.subjects.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Phase: Running */}
          {phase === 'running' && (
            <div className="import-running">
              <div className="import-spinner" />
              <p>Processing modules...</p>
            </div>
          )}

          {/* Phase: Dry Run Results */}
          {phase === 'dryrun' && dryRunResult && (
            <div className="import-dryrun">
              <h3>Preview Changes</h3>
              <ImportSummaryCards
                result={dryRunResult}
                activeFilter={statusFilter}
                onFilter={setStatusFilter}
                labels={{ created: 'To Create', updated: 'To Update', unchanged: 'Unchanged', error: 'Errors' }}
              />
              <ImportModuleList modules={dryRunResult.modules} filter={statusFilter} />
            </div>
          )}

          {/* Phase: Final Results */}
          {phase === 'results' && finalResult && (
            <div className="import-results">
              <h3>Import Complete</h3>
              <ImportSummaryCards
                result={finalResult}
                activeFilter={statusFilter}
                onFilter={setStatusFilter}
                labels={{ created: 'Created', updated: 'Updated', unchanged: 'Unchanged', error: 'Errors' }}
              />
              <ImportModuleList modules={finalResult.modules} filter={statusFilter} />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          {phase === 'configure' && (
            <>
              <button className="btn-cancel" onClick={handleClose}>Cancel</button>
              <button
                className="btn-save"
                onClick={handleDryRun}
                disabled={parsedModules.length === 0}
              >
                Preview Changes
              </button>
            </>
          )}
          {phase === 'dryrun' && (
            <>
              <button className="btn-cancel" onClick={() => { reset(); }}>
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleImport}
                disabled={dryRunResult?.created === 0 && dryRunResult?.updated === 0}
              >
                Import {(dryRunResult?.created ?? 0) + (dryRunResult?.updated ?? 0)} Modules
              </button>
            </>
          )}
          {phase === 'results' && (
            <>
              <button className="btn-cancel" onClick={reset}>Import Another</button>
              <button className="btn-save" onClick={handleClose}>Close</button>
            </>
          )}
        </div>
    </Modal>
  );
}

function ModuleStatusRow({ mod }: { mod: ModuleImportStatus }) {
  const statusIcon = mod.status === 'created' ? '+' : mod.status === 'updated' ? '~' : mod.status === 'error' ? '!' : '-';
  const statusClass = `import-module-row import-module-row--${mod.status}`;

  return (
    <div className={statusClass}>
      <span className="import-module-status">{statusIcon}</span>
      <span className="import-module-rid">{mod.readableId}</span>
      <span className="import-module-name">{mod.displayName}</span>
      {mod.error && <span className="import-module-error">{mod.error}</span>}
      {mod.changes && mod.changes.length > 0 && (
        <div className="import-module-changes">
          {mod.changes.map((c, i) => (
            <span key={i} className="import-change">
              <span className="import-change-field">{c.field}:</span>
              <span className="import-change-from">{formatValue(c.from)}</span>
              <span className="import-change-arrow">&rarr;</span>
              <span className="import-change-to">{formatValue(c.to)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === undefined || v === null) return '(empty)';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

type StatusType = 'created' | 'updated' | 'unchanged' | 'error';

function ImportSummaryCards({ result, activeFilter, onFilter, labels }: {
  result: ModuleImportResult;
  activeFilter: StatusType | null;
  onFilter: (f: StatusType | null) => void;
  labels: Record<StatusType, string>;
}) {
  const toggle = (s: StatusType) => onFilter(activeFilter === s ? null : s);
  const cards: { status: StatusType; count: number; show: boolean }[] = [
    { status: 'created', count: result.created, show: true },
    { status: 'updated', count: result.updated, show: true },
    { status: 'unchanged', count: result.unchanged, show: true },
    { status: 'error', count: result.errors, show: result.errors > 0 },
  ];
  return (
    <div className="import-summary-grid">
      {cards.filter(c => c.show).map(c => (
        <div
          key={c.status}
          className={`import-stat import-stat--${c.status} ${activeFilter === c.status ? 'import-stat--active' : ''}`}
          onClick={() => toggle(c.status)}
          style={{ cursor: 'pointer' }}
        >
          <span className="import-stat-value">{c.count}</span>
          <span className="import-stat-label">{labels[c.status]}</span>
        </div>
      ))}
    </div>
  );
}

function ImportModuleList({ modules, filter }: { modules: ModuleImportStatus[]; filter: StatusType | null }) {
  const filtered = filter ? modules.filter(m => m.status === filter) : modules.filter(m => m.status !== 'unchanged');
  return (
    <div className="import-details">
      {filtered.map((mod, i) => (
        <ModuleStatusRow key={i} mod={mod} />
      ))}
      {filtered.length === 0 && (
        <p style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 16 }}>
          {filter ? `No ${filter} modules` : 'No changes'}
        </p>
      )}
    </div>
  );
}
